import i18n from '@/i18n';
import { languageFromTitle } from '@/utils/languageFromTitle';

/**
 * Language code to display name mapping
 * Used for translation UI across the app
 */
export const LANGUAGE_NAMES: Record<string, string> = {
  EN: 'English',
  ES: 'Spanish',
  DE: 'German',
  FR: 'French',
  IT: 'Italian',
  PT: 'Portuguese',
  NL: 'Dutch',
  JA: 'Japanese',
  ZH: 'Chinese',
  KO: 'Korean',
  AR: 'Arabic',
  RU: 'Russian',
  PL: 'Polish',
};

/**
 * Localized name of a language code in the UI locale ("de" -> "Deutsch" in German, "German" in English). Uses
 * Intl.DisplayNames; falls back to the English table (then the upper-cased code) when the runtime lacks the data.
 */
export function localizedLanguageName(code: string, locale: string = i18n.language): string {
  const cacheKey = `${locale}|${code}`;
  const cached = languageNameCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const resolved = resolveLocalizedLanguageName(code, locale);
  languageNameCache.set(cacheKey, resolved);
  return resolved;
}

/** Intl.DisplayNames construction is expensive: memoize per (locale, code) and reuse one instance per locale. */
const languageNameCache = new Map<string, string>();
const displayNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function resolveLocalizedLanguageName(code: string, locale: string): string {
  const english = LANGUAGE_NAMES[code.toUpperCase()];
  try {
    const key = locale || 'en';
    if (!displayNamesByLocale.has(key)) displayNamesByLocale.set(key, new Intl.DisplayNames([key], { type: 'language' }));
    const name = displayNamesByLocale.get(key)?.of(code.toLowerCase());
    if (name && name.toLowerCase() !== code.toLowerCase()) {
      return name.charAt(0).toLocaleUpperCase(locale || 'en') + name.slice(1);
    }
  } catch {
    // invalid locale/code: fall through to the English table
  }
  return english ?? code.toUpperCase();
}

/**
 * Get display name for a language code (localized to the UI language)
 */
export function getLanguageName(code: string): string {
  return localizedLanguageName(code);
}

/**
 * All supported language codes
 */
export const SUPPORTED_LANGUAGES = Object.keys(LANGUAGE_NAMES);

/**
 * SSOT: is this stored value a language the app can label? "de", "pt-br" yes; null, "", "xx" no. Only supported
 * codes may be turned into a language label/badge/filter option; an unknown stored value stays intact in the data
 * and the edit form but is never rendered as a label.
 */
export function isSupportedLanguage(language: string | null | undefined): language is string {
  if (!language) return false;
  const base = language.trim().toLowerCase().split(/[-_]/)[0].toUpperCase();
  return Object.prototype.hasOwnProperty.call(LANGUAGE_NAMES, base);
}

/** Upper-case badge code ("DE") for a supported language, null for none/unknown (render no badge). */
export function languageBadgeCode(language: string | null | undefined): string | null {
  return isSupportedLanguage(language) ? language.trim().toUpperCase() : null;
}

/**
 * Display name for a stored (lowercase) language code: the English name when the app knows it, else the
 * upper-cased code. Used by the Release Board filter.
 */
export function languageDisplayName(code: string): string {
  return localizedLanguageName(code);
}

const APP_LOCALES: readonly string[] = ['en', 'de', 'es', 'fr', 'pl'];

/** Lowercase, strip accents, "l-stroke" to l, "sharp s" to ss. */
const foldLabel = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .toLowerCase();

const escapeRegExp = (raw: string): string => raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Whole-token matcher on Unicode word boundaries (so "Made" / "Demo" never match "de"). NO lookbehind: it throws a
 * SyntaxError at construction on Safari < 16.4, and this runs during render. The leading boundary is a capture group
 * instead. Falls back to an ASCII word-boundary regex when `\p{}` / the `u` flag is unsupported.
 */
const tokenRegExp = (body: string, flags: string): RegExp => {
  try {
    return new RegExp(`(^|[^\\p{L}\\p{N}])(?:${body})(?![\\p{L}\\p{N}])`, flags);
  } catch {
    return new RegExp(`(^|[^A-Za-z0-9])(?:${body})(?![A-Za-z0-9])`, flags.replace('u', ''));
  }
};

/** Code as the LAST token (no lookbehind, same Safari rationale as tokenRegExp). */
const finalTokenRegExp = (body: string): RegExp => {
  try {
    return new RegExp(`(^|[^\\p{L}\\p{N}])${body}$`, 'iu');
  } catch {
    return new RegExp(`(^|[^A-Za-z0-9])${body}$`, 'i');
  }
};

/**
 * True when `title` already names `language` as a whole token anywhere in it ("ZZ-CX review DE", "Deutsch teaser",
 * "Teaser (German)", "DE - PIC 33"). Matches the code and the language NAME in English, the UI language and every
 * app locale, accent- and case-insensitively. Codes are ordinary words in some languages ("de", "it"), so a
 * lowercase code only counts as the FINAL token ("review de"); an UPPERCASE code counts anywhere.
 *
 * Known false-positive rules (deliberate): an UPPERCASE code anywhere counts ("IT roadmap", "DE" in "AI DE-brief"
 * would match "de"), and a language NAME counts even when it is a different sense ("Polish the copy"). A false
 * positive only SKIPS appending " · XX" to the label, so it is the safe direction; a lowercase code mid-title
 * ("de" in "Made de Paris") never matches.
 */
export function titleMentionsLanguage(title: string, language: string): boolean {
  const base = language.trim().toLowerCase().slice(0, 2);
  if (base.length < 2) return false;

  const names = new Set<string>();
  const english = LANGUAGE_NAMES[base.toUpperCase()];
  if (english) names.add(foldLabel(english));
  for (const locale of [...APP_LOCALES, i18n.language]) {
    const name = localizedLanguageName(base, locale);
    if (name.toLowerCase() !== base) names.add(foldLabel(name));
  }
  if (names.size > 0 && tokenRegExp([...names].map(escapeRegExp).join('|'), 'u').test(foldLabel(title))) return true;

  if (tokenRegExp(escapeRegExp(base.toUpperCase()), 'u').test(title)) return true;
  const trailing = title.trim().replace(/[\s)\]—–\-:·|/.]+$/, '');
  return finalTokenRegExp(escapeRegExp(base)).test(trailing);
}

/**
 * SSOT for every release-version label (drawer chips + dropdown, board, composer): the version name with its
 * language when known, never repeated when the title already carries it: "Original · DE", "German Version",
 * "ZZ-CX review DE". An unknown/unsupported language adds nothing (never guessed).
 */
export function versionNameWithLanguage(name: string, language: string | null): string {
  if (!isSupportedLanguage(language)) return name;
  const code = language.toUpperCase();
  if (name.trim().toUpperCase() === code) return name;
  const base = language.trim().toLowerCase().slice(0, 2);
  // A title that is just the English language name ("German") reads in the UI language ("Deutsch").
  if (name.trim().toLowerCase() === (LANGUAGE_NAMES[base.toUpperCase()] ?? '').toLowerCase()) {
    return localizedLanguageName(base);
  }
  // The title already names this language ("DE - PIC 33", "German version", "ZZ-CX review DE"): do not repeat it.
  if (languageFromTitle(name)?.slice(0, 2) === base || titleMentionsLanguage(name, language)) return name;
  return `${name} · ${code}`;
}

/**
 * Shape rule for a stored language code: MIRRORS `LANGUAGE_CODE_PATTERN` in
 * backend/src/utils/variation-language.ts (change both together). "de", "pt-br" yes; "english" no.
 */
export const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}(-[a-z0-9]{2,4})?$/;
export const LANGUAGE_CODE_MAX_LENGTH = 10;

/** Trivial normalisation (trim, lowercase, "_" to "-") then the shape rule; null when it is not a code. */
export function parseLanguageCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toLowerCase().replace(/_/g, '-');
  return code.length <= LANGUAGE_CODE_MAX_LENGTH && LANGUAGE_CODE_PATTERN.test(code) ? code : null;
}
